import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { FaClock } from "react-icons/fa";

const ShiftPage = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/v1/shift`;
  const token = localStorage.getItem("token");

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [totalData, setTotalData] = useState(0);

  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(role);
    if (role !== "owner") setShowAccessDenied(true);
  }, []);

  useEffect(() => {
    const fetchShifts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_URL}?page=${page}&per_page=${perPage}&order_by=${sortOption}&search=${searchTerm}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error("Gagal mengambil data shift.");
        const data = await response.json();
        setShifts(data.data);
        setLastPage(data.meta.last_page);
        setTotalData(data.meta.total);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchShifts();
  }, [page, perPage, sortOption, searchTerm]);

  return (
   <div className="min-h-screen flex flex-col space-y-6">

      {/* ============================
          HEADER STYLE (MATCH OUTLET)
      ============================ */}
      <div className="bg-white shadow-lg rounded-2xl p-5 border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-green-100 rounded-2xl shadow-inner">
            <FaClock className="text-green-600 text-xl" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Shift</h1>
            <p className="text-gray-500 text-sm">Menampilkan semua data Shift</p>
          </div>
        </div>

        {/* BREADCRUMB */}
        <nav aria-label="Breadcrumb" className="mt-3 text-gray-500 text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <button
                onClick={() => navigate("/")}
                className="hover:text-gray-700 transition"
              >
                Enterprise
              </button>
            </li>
            <li>/</li>
            <li className="text-gray-400">Shift</li>
          </ol>
        </nav>
      </div>

      {/* ========================
          ACTION BAR (MATCHED)
      ======================== */}
      <div className="bg-white p-6 rounded-3xl shadow-xl border border-gray-100">

        <div className="flex flex-col md:flex-row items-center gap-4">

          {/* SEARCH */}
          <div className="relative w-full md:flex-1">
            <input
              type="text"
              placeholder="Cari shift..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-green-300 focus:outline-none"
            />
            <FiSearch className="absolute left-3 top-3.5 text-gray-400" />
          </div>

          {/* SORT */}
          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="px-4 py-3 bg-white border border-gray-300 rounded-xl shadow hover:shadow-md transition active:scale-95"
          >
            <option value="">Urutkan</option>
            <option value="asc">Tanggal (Asc)</option>
            <option value="desc">Tanggal (Desc)</option>
          </select>

          {/* CREATE */}
          <button
            onClick={() => navigate("/enterprise/shift/create-shift")}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-white rounded-xl shadow hover:bg-yellow-700 hover:shadow-md transition active:scale-95"
          >
            <FiPlus />
            <span>Buat Shift</span>
          </button>
        </div>

        {/* ========================
            TABLE (MATCHED STYLE)
        ======================== */}
        <div className="mt-6">
          {loading ? (
            <div className="text-center py-10 text-gray-600">Loading data...</div>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
              <table className="min-w-full bg-white rounded-2xl">
                <thead className="bg-green-50">
                  <tr>
                    <th className="p-4 text-left font-semibold text-gray-700 border-b">No</th>
                    <th className="p-4 text-left font-semibold text-gray-700 border-b">Nama</th>
                    <th className="p-4 text-left font-semibold text-gray-700 border-b">Kode</th>
                    <th className="p-4 text-left font-semibold text-gray-700 border-b">Jam Mulai</th>
                    <th className="p-4 text-left font-semibold text-gray-700 border-b">Jam Pulang</th>
                    <th className="p-4 text-left font-semibold text-gray-700 border-b">Aksi</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {shifts.map((shift, index) => (
                    <tr key={shift.id} className="hover:bg-gray-50 transition">
                      <td className="p-4">{(page - 1) * perPage + index + 1}</td>
                      <td className="p-4 font-medium text-gray-800">{shift.name}</td>

                      {/* BADGE CODE */}
                      <td className="p-4">
                        <span
                          className={`px-3 py-1 rounded-full text-white font-semibold text-xs ${
                            shift.code === "P"
                              ? "bg-green-500"
                              : shift.code === "S"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                          }`}
                        >
                          {shift.code}
                        </span>
                      </td>

                      <td className="p-4 text-gray-700">{shift.start_time}</td>
                      <td className="p-4 text-gray-700">{shift.end_time}</td>

                      {/* AKSI BUTTON SAME STYLE AS OUTLET */}
                      <td className="p-4">
                        <button
                          className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg shadow-md text-blue-600 hover:text-blue-700 transition transform hover:scale-110"
                          onClick={() =>
                            navigate(`/enterprise/shift/edit-shift/${shift.id}`)
                          }
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION (MATCH OUTLET) */}
          <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-3">
            <p className="text-sm text-gray-500">
              Menampilkan {shifts.length} data dari total {totalData}
            </p>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className="flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-gray-700 rounded-xl shadow-sm transition disabled:opacity-50 active:scale-95"
              >
                <FiChevronLeft className="mr-1" />
                Prev
              </button>

              <span className="text-sm text-gray-600">
                Halaman <strong>{page}</strong> dari{" "}
                <strong>{lastPage}</strong>
              </span>

              <button
                onClick={() => setPage((prev) => Math.min(prev + 1, lastPage))}
                disabled={page === lastPage}
                className="flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-gray-700 rounded-xl shadow-sm transition disabled:opacity-50 active:scale-95"
              >
                Next
                <FiChevronRight className="ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ========================
          MODAL AKSES DITOLAK
      ======================== */}
      {showAccessDenied && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-lg w-[90%] max-w-md">
            <h2 className="text-xl font-bold mb-3 text-center text-red-600">
              Akses Ditolak
            </h2>
            <p className="text-gray-600 text-center mb-5">
              Anda tidak memiliki hak akses untuk memasuki halaman ini.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition"
            >
              Kembali ke Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftPage;
